import {ContextMenuOption} from 'components/ContextMenu';
import {SelectionRange} from 'notebook/tables/constants/TableSelectionConstants';
import {TableModel} from 'notebook/tables/store/models/TableModel';
import {createDebuggableSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {
  createTableEditableStoreStructuredSelector,
  TableEditableStore,
} from 'notebook/tables/store/TableStore';

/**
 * The table-level values a row renders with. These are identical for every row, but they are
 * relayed through the row model so that a row has everything it needs to render in one place
 * rather than reaching back into the {@link TableModel} for each value individually.
 */
export interface RowTableModel {
  /** The table's own cid, used to build the `data-cid` of the cells on this row. */
  cid: string;
  /** Whether the table as a whole is locked. */
  isTableLocked: boolean;
  /** Whether the table is protected, which restricts the row axis cell's context menu. */
  isProtected: boolean;
  /** Whether the row axis cells should render excess labels. */
  showRowExcessLabels: boolean;
  /** Whether the row header renders left-aligned. */
  isLeftAlignRowHeader: boolean;
  /**
   * The number of ghost cells to render after the row's real cells. Always zero in the
   * single-input view, which has no ghost cells.
   */
  ghostCellsCount: number;
  /** Whether the table is allowed to have zero rows. */
  allowNoRows: boolean;
  /**
   * Supplies table-specific extra options for the row axis cell's context menu, when the embedding
   * feature provides any.
   */
  getExtraRowAxisContextMenuOptions?: (
    selectionRange: SelectionRange | null
  ) => Array<ContextMenuOption> | null;

  /**
   * The number of leading columns frozen to the left edge. Always zero in the single-input view,
   * which renders its own columns and never affixes them.
   */
  affixedColumnCount: number;
  /** Whether the user has frozen any columns. Always false in the single-input view. */
  hasAnyFrozenColumns: boolean;
  /**
   * The width (in px) of the leading axis column, or null in the single-input view, which has no
   * axis column. Zero means the table renders without one.
   */
  firstColumnWidth: number | null;
  /**
   * The width (in px) of each of the table's data columns, or null in the single-input view, whose
   * column widths are managed separately.
   */
  columnWidths: Array<number> | null;
  /**
   * Whether the last affixed column should cast its shadow over the scrolled content. Always false
   * in the single-input view.
   */
  showAffixedShadow: boolean;
}

/**
 * Creates a selector that collects the table-level values a single row renders with
 * ({@link RowTableModel}) from the {@link TableModel}.
 */
export function createRowTableSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  isSingleInput: boolean
): (store: TableEditableStore) => RowTableModel {
  return createTableEditableStoreStructuredSelector(
    {
      cid: (store) => tableSelector(store).props.cid,
      isTableLocked: (store) => tableSelector(store).locking.isTableLocked,
      // `props` holds TableEditable's raw props, so the `??` defaults here repeat the ones
      // `TableEditable`'s `propsWithDefaults` applies before rendering the class component.
      isProtected: (store) => tableSelector(store).props.isProtected ?? false,
      showRowExcessLabels: (store) => tableSelector(store).visibility.showRowExcessLabels,
      isLeftAlignRowHeader: (store) => tableSelector(store).props.isLeftAlignRowHeader ?? false,
      ghostCellsCount: (store) => (isSingleInput ? 0 : tableSelector(store).dimensions.ghostCellsCount),
      allowNoRows: (store) => tableSelector(store).dimensions.allowNoRows,
      getExtraRowAxisContextMenuOptions: (store) =>
        tableSelector(store).props.getExtraRowAxisContextMenuOptions,
      // The affixed-column cluster is only meaningful for rows in the main table; single-input rows
      // are rendered outside the scrollable table and never freeze columns.
      affixedColumnCount: (store) => (isSingleInput ? 0 : tableSelector(store).affixed.affixedColumnCount),
      hasAnyFrozenColumns: (store) =>
        isSingleInput ? false : tableSelector(store).affixed.hasAnyFrozenColumns,
      firstColumnWidth: (store) =>
        isSingleInput ? null : tableSelector(store).measurements.firstColumnWidth,
      columnWidths: (store) => (isSingleInput ? null : tableSelector(store).measurements.columnWidths),
      showAffixedShadow: (store) => (isSingleInput ? false : tableSelector(store).affixed.showAffixedShadow),
    },
    createDebuggableSelector.withTypes<TableEditableStore>()
  );
}
